/**
 * Was die Gerätedetailseite **für dieses Gerät** anbietet (HM-113).
 *
 * **Bis hierher hatte die Seite eine Gestalt, und das war die des Lesekopfs.**
 * Ein Gateway bekam dieselben elf Handlungen, dasselbe Formular mit
 * Home-Assistant-Schalter und Update-Kanal und darüber den Rat, das Gerät neu
 * zu starten — obwohl es keine Fernsteuerung anbietet. Und ein Gerät hinter dem
 * Gateway, eine Wallbox etwa, bekam „Neu starten", „Update installieren" und
 * einen roten Kasten „Gerät freigeben": Die API antwortete auf alles davon mit
 * `409`, denn Aufträge, Zugangsdaten und Freigabe gehören dort dem Gateway.
 * Eine Schaltfläche, die zuverlässig abgelehnt wird, ist kein Angebot, sondern
 * eine Falle.
 *
 * **Unterschieden wird nach der Verbindung, nicht nach dem Erzeugnis.** Drei
 * Klassen tragen alles, was sich auf der Seite unterscheidet:
 *
 * | Klasse           | erkennbar an                  | Aufträge, Freigabe      |
 * | ---------------- | ----------------------------- | ----------------------- |
 * | `own_connection` | eigene Verbindung zum Broker  | am Gerät, nach Fähigkeit |
 * | `gateway`        | `hausmeter_gateway`           | nur gemeldete Fähigkeiten |
 * | `gateway_node`   | `gatewayDeviceId` gesetzt     | keine; Ablehnen am Gateway |
 *
 * Ein neues Erzeugnis mit eigener Verbindung braucht hier deshalb keine Zeile,
 * und eines hinter dem Gateway ebenso wenig. **Was ein einzelnes Erzeugnis
 * zusätzlich kann**, steht daneben und nicht in einer vierten Klasse: Die
 * Wallbox-Ansicht hängt am Typ `evcc_loadpoint` (`device-type.ts`), weil ihre
 * Deutung die von EVCC ist.
 *
 * **`gatewayDeviceId` ist der Vertrag, nicht eine Vermutung** (`DeviceSchema`):
 * „Ein Client liest daran ab, dass dieses Gerät keine eigenen Zugangsdaten hat."
 * Das Gateway selbst trägt dort `null` und wird an seinem Typ erkannt — ein
 * Vergleich mit einer Kennung, kein Blick in einen Schlüssel.
 *
 * **Hier und nicht in der Komponente**, weil es reine Rechnung ist und geprüft
 * sein will: Eine Bedingung, die an drei Stellen der Seite steht, ist an einer
 * davon bald eine andere.
 */
import type { ApiComponents } from "@hausmeter/sdk";
import { isGatewayDevice } from "./device-type.js";

type Device = ApiComponents["schemas"]["Device"];

export type DeviceClass = "own_connection" | "gateway" | "gateway_node";

export function deviceClass(device: Pick<Device, "deviceType" | "gatewayDeviceId">): DeviceClass {
  if (device.gatewayDeviceId !== null) return "gateway_node";
  if (isGatewayDevice(device.deviceType)) return "gateway";
  return "own_connection";
}

/**
 * Welche Schlüssel sich aus der Ferne setzen lassen — `null` heißt „alle".
 *
 * **Beim Gerät mit eigener Verbindung heißt Schweigen „setzbar"** (Regel 9,
 * `canSetRemotely`): Ein Lesekopf, der seine Fähigkeiten noch nicht gemeldet
 * hat, hat nicht gesagt, dass er nichts kann.
 *
 * **Beim Gateway heißt Schweigen „nichts".** Das ist keine strengere Lesart
 * derselben Regel, sondern sein Vertrag: Der Auftrag an das Gateway verlangt,
 * `capabilities` wegzulassen, solange es keine Fernsteuerung gibt, und sagt
 * dazu, die Oberfläche zeige dann keine Aktionen
 * (`docs/device/gateway-cloud-prompt.md`, Abschnitt 4). Meldet es eines Tages
 * welche, gelten genau diese.
 *
 * **Ein Knoten nimmt keine Konfiguration an** — die API weist sie mit
 * `device_command.node_not_addressable` ab.
 */
export function remoteWritableKeys(device: Device): readonly string[] | null {
  switch (deviceClass(device)) {
    case "own_connection":
      return device.capabilities?.writable ?? null;
    case "gateway":
      return device.capabilities?.writable ?? [];
    case "gateway_node":
      return [];
  }
}

/**
 * Ob das Gerät eine Aktion (`/commands`) annimmt — dieselbe Unterscheidung wie
 * bei den Schlüsseln.
 */
export function offersCommand(device: Device, action: string): boolean {
  switch (deviceClass(device)) {
    case "own_connection": {
      const commands = device.capabilities?.commands ?? null;
      return commands === null || commands.includes(action);
    }
    case "gateway":
      return device.capabilities?.commands.includes(action) ?? false;
    case "gateway_node":
      return false;
  }
}

/**
 * Was die Seite zeigt. Jede Angabe ist eine Frage, die die Seite an einer
 * Stelle stellt — und die Antwort steht nur hier.
 */
export type DeviceSurface = {
  readonly deviceClass: DeviceClass;
  /**
   * Die Datenpunktliste auf der Übersicht. **Nicht beim Gateway**: Es misst
   * nichts selbst; was hinter ihm gemessen wird, steht an den Geräten dahinter,
   * und die stehen an seiner Stelle.
   */
  readonly datapoints: boolean;
  /** Die Geräte hinter diesem Gateway — Fund, Zulassung, Ablehnung. */
  readonly nodes: boolean;
  /** Das Konfigurationsformular. Ohne setzbaren Schlüssel gibt es keines. */
  readonly configForm: boolean;
  /**
   * „Nach einem Update suchen" und „Update installieren". **Nur mit eigener
   * Verbindung**: `/ota` kennt keine Fähigkeit, an der ein Gateway sein Angebot
   * melden könnte — und sein Auftrag sieht keines vor.
   */
  readonly firmware: boolean;
  /** Ob überhaupt eine Handlung auf der Seite steht, die einen Auftrag erzeugt. */
  readonly sendsCommands: boolean;
  /**
   * Wie das Gerät die Anlage verlässt. `release` widerruft eigene Zugangsdaten;
   * `dismiss` lehnt einen Knoten am Gateway ab — freigeben lässt er sich nicht
   * (`409 device.gateway_node`). `null` für ein **schon freigegebenes** Gerät,
   * das über einen alten Link geöffnet wird: Ein zweites „Freigeben" täte nichts
   * und sähe aus, als hätte das erste nicht gewirkt.
   */
  readonly leave: "release" | "dismiss" | null;
};

/**
 * Die Wallbox schreibt an Datenpunkte, und das tut auch ein Knoten — deshalb
 * zählt ein schreibbarer Datenpunkt für `sendsCommands`, und die Seite gibt ihn
 * herein, weil der Katalog in einer eigenen Abfrage steht.
 */
export function deviceSurface(
  device: Device,
  options: { readonly hasWritableDatapoint?: boolean } = {},
): DeviceSurface {
  const kind = deviceClass(device);
  const writable = remoteWritableKeys(device);
  const configForm = writable === null || writable.length > 0;
  const commands = device.capabilities?.commands ?? null;
  const anyCommand =
    kind === "own_connection" ? true : kind === "gateway" ? (commands?.length ?? 0) > 0 : false;
  const firmware = kind === "own_connection";

  return {
    deviceClass: kind,
    datapoints: kind !== "gateway",
    nodes: kind === "gateway",
    configForm: kind !== "gateway_node" && configForm,
    firmware,
    sendsCommands:
      firmware ||
      anyCommand ||
      (kind !== "gateway_node" && configForm) ||
      options.hasWritableDatapoint === true,
    leave: device.claimStatus === "released" ? null : kind === "gateway_node" ? "dismiss" : "release",
  };
}
